import { isNil, cloneDeep, orderBy, max, uniqBy, take } from 'lodash'
import produce from 'immer'
import { Button, Tooltip, message } from 'antd'
import { createChildListViewComponent } from 'factories/ChildListView'
import entityNames from 'options/entityNames'
import { isReadOnly } from 'helpers/formViews'
import { t } from 'helpers/i18n'
import { Emitter } from 'helpers/events'
import { LIST_MODAL_CLEAR_SELECTION } from 'options/events'
import FormView from 'containers/Rfqs/Suppliers/FormView'
import Checkbox from 'elements/Checkbox'
import DatePicker from 'elements/DatePicker'
import Icon from 'elements/Icon'
import Modal from 'elements/Modal'
import SelectSupplier from 'containers/Suppliers/Select'

export default createChildListViewComponent({
  entityName: entityNames.rfqs,
  childName: 'rfqSuppliers',
  allowCustomize: (self) => false,
  allowCreate: (self) => false,
  allowDelete: (self) => true,
  allowPagination: (self) => false,
  createButtonTextId: 'addSupplierToRfq',
  primaryLinkTarget: 'rfqSupplierRecord',
  createButtonLinkTarget: {
    formSize: 'md',
    formComponent: (props) => <FormView {...props} settingsType="rfq" />,
  },
  extraTableButtons: (self) => (
    <>
      <Tooltip
        title={t(self.props.items.length >= 10 ? 'maximumSuppliersAdded' : 'addSupplierToRfq')}
        placement="topLeft"
      >
        <Button
          type="primary"
          size="small"
          onClick={() => self.setState({ addSupplierToRfqVisible: true, addSupplierToRfqRecords: null })}
          disabled={isReadOnly(self) || self.props.items.length >= 10}
        >
          <Icon type="Add" bold />
        </Button>
      </Tooltip>
      <Modal
        title={t('addSupplierToRfq')}
        visible={self.state.addSupplierToRfqVisible}
        okText={t('add')}
        okButtonProps={{
          disabled: isNil(self.state.addSupplierToRfqRecords) || self.props.items.length >= 10,
          loading: self.state.addSupplierToRfqLoading,
        }}
        onOk={() => {
          const siblings = cloneDeep(orderBy(self.props.items, ['supplierPosition']))

          siblings.forEach((each, index) => {
            each.supplierPosition = index
          })

          const before = siblings.length
          const supplierPosition = max([...siblings.map((each) => each.supplierPosition), -1]) + 1
          const seed = Date.now() * -1

          self.state.addSupplierToRfqRecords.forEach((each, index) => {
            const {
              name: supplierName,
              contactName: supplierContact,
              id: supplierId,
              contactEmail: supplierEmail,
              contactPhone: supplierPhone,
            } = each

            siblings.push({
              id: seed - index,
              rfqSupplierId: seed - index,
              supplierName,
              supplierContact,
              supplierPhone,
              supplierEmail,
              promiseDate: null,
              comment: '',
              displaySupplier: true,
              rfqId: self.props.parentRecord.id,
              supplierId,
              supplierPosition: supplierPosition + index,
              freightCost: 0,
            })
          })

          const suppliers = take(uniqBy(siblings, 'supplierId'), 10)

          self.props.onChange?.(suppliers)

          const added = suppliers.length - before

          message[added > 0 ? 'success' : 'warn'](
            `${added} ${t('of')} ${self.state.addSupplierToRfqRecords.length} ${t('suppliersAdded')}.`
          )

          if (suppliers.length >= 10) {
            message.warn(t('maximumSuppliersAdded'))

            self.setState({ addSupplierToRfqVisible: false })
          }

          Emitter.emit(LIST_MODAL_CLEAR_SELECTION)
        }}
        cancelText={t('close')}
        onCancel={() => self.setState({ addSupplierToRfqVisible: false })}
        width={992}
      >
        <SelectSupplier
          onSelect={(values) => self.setState({ addSupplierToRfqRecords: values })}
          filterProps={{ disabled: true }}
          mode="multiple"
        />
      </Modal>
    </>
  ),
  tableCellComponents: {
    displaySupplier: (self, item) => (
      <Checkbox
        checked={item.displaySupplier}
        onChange={async (e) => {
          try {
            await self.props.onChange(
              produce(self.props.items, (draft) => {
                draft.find((one) => one.id === item.id).displaySupplier = e.target.checked
              })
            )
          } catch (error) {
            console.warn(error)
          }
        }}
        readOnly={isReadOnly(self)}
      />
    ),
    promiseDate: (self, item) => (
      <DatePicker
        value={item.promiseDate}
        onChange={async (value) => {
          try {
            await self.props.onChange(
              produce(self.props.items, (draft) => {
                draft.find((one) => one.id === item.id).promiseDate = value
              })
            )
          } catch (error) {
            console.warn(error)
          }
        }}
        readOnly={isReadOnly(self)}
        placeholder={t('none')}
        allowClear
      />
    ),
  },
})()
